import { useCallback, useEffect, useState } from "react";
import { RING_STATUS, resolveRingTruth } from "score-common";
import { useScoreApi, unwrap, stationBody } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { readCache } from "../local/db";
import { getPresenceInstanceId } from "../util/presenceInstanceId";

const ringCacheKey = (showId) => `ringState:${showId}`;
const writeRingCache = (showId, byRing) => {
  readCache.put(ringCacheKey(showId), byRing).catch(() => {});
};
// One ring's row merged into the cached map for the show, so a later cache
// repaint (show re-entry, an `online` change) shows this row, not an older one.
const mergeRingCache = (showId, ring, row) => {
  readCache
    .get(ringCacheKey(showId))
    .then((prev) => writeRingCache(showId, { ...(prev ?? {}), [ring]: row }))
    .catch(() => {});
};

// #1935 (docs/score/ring-state.md) — ring state for the scribe's class
// list: the per-class "Start class" action. Every write here is OPTIONAL
// and fire-and-forget (card constraint, AC6): nothing in this hook can
// block, gate, delay, or interrupt scoring, there is no confirmation
// dialog, and a failed write is swallowed silently (AC7) — the scribe
// simply sees no "current" indication, exactly as if she had never
// pressed the button (AC2's derived-behaviour fallback still holds
// because the write path IS the fallback, per docs/score/ring-state.md).
//
// AC4 — an explicit set (Start class) always OVERWRITES whatever a ring
// currently shows, silently, with no confirmation (the card's own
// constraint forbids one). This mirrors #1934's `setRingStateExplicit`,
// which has no merge rule: "an explicit set always wins at the instant of
// the press" (docs/score/ring-state.md). Decided and written down here
// per AC4, not left to guesswork.
//
// #2048 — the ring-level Break/End of day BAR (ring name + status chip +
// two buttons) was Score Admin's job, not the scribe's, and #2048 removed
// it along with declareBreak/declareEndOfDay/statusForRing. #2109
// (docs/score/ring-truth.md rule 4) is a different, narrower thing: an
// explicit End action for the scribe's OWN current class, sitting beside
// Start class on the ride list (#2081's pattern), not a ring-wide bar. See
// endClass below.
export function useRingState(showId) {
  const scoreApi = useScoreApi();
  const [ringStateByRing, setRingStateByRing] = useState({});

  // card #3196 (docs/score/scribe-resilience.md I8) - ring state reads
  // cache-first: the last answer for this show paints from IndexedDB first,
  // the network (only while online) refines it, and a failed read keeps what
  // is painted instead of blanking it. With nothing cached the overlay is
  // simply absent, same as before (AC2 of #1935).
  const { online } = useScoreContext();
  const refetch = useCallback(async () => {
    if (!showId) {
      setRingStateByRing({});
      return;
    }
    if (!online) return;
    try {
      const rows = unwrap(await scoreApi.ringState.listRingStates(showId));
      const byRing = {};
      for (const row of rows || []) {
        if (row?.ring) byRing[row.ring] = row;
      }
      setRingStateByRing(byRing);
      writeRingCache(showId, byRing);
    } catch {
      // AC7 - a failed READ is silent too, and keeps the cached overlay.
    }
  }, [showId, scoreApi, online]);

  // Another show's rings are not this show's: drop the previous overlay when the
  // show changes, so show B can never paint show A's "Now showing in Ring 1".
  // Keyed on showId alone (not on refetch, which also changes with `online`).
  useEffect(() => {
    setRingStateByRing({});
  }, [showId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (showId) {
        let cached;
        try {
          cached = await readCache.get(ringCacheKey(showId));
        } catch {
          cached = undefined;
        }
        if (!cancelled && cached) setRingStateByRing(cached);
      }
      if (!cancelled) await refetch();
    })();
    return () => {
      cancelled = true;
    };
  }, [showId, refetch]);

  // #2109 (docs/score/ring-truth.md) — "already current" for a ring is
  // rules 5+8's question, answered by score-common's resolveRingTruth
  // (the SAME resolver score-api's own getRingTruth composes), not by
  // hand-checking row.status/row.classId here. This is scribe-fe's only
  // read of ring truth, so every "is this class current" question in the
  // app (ClassList's chip, RideList's Start/End toggle) funnels through
  // this one function. Source (EXPLICIT vs SCORED) doesn't matter per rule
  // 6/8 — resolveRingTruth already ignores it.
  const isCurrentForRing = useCallback(
    (ring, classId) => {
      if (!ring || !classId) return false;
      const truth = resolveRingTruth(ringStateByRing[ring]);
      return truth.inProgress && truth.classId === classId;
    },
    [ringStateByRing]
  );

  const setExplicit = useCallback(
    async (ring, { status, classId = null, rideId = null }) => {
      if (!showId || !ring) return;
      try {
        const item = unwrap(
          // #3572 - send THIS station's GUID as `writerDeviceId` (the same source
          // every scribe write is stamped from, #3519) so score-api can tell a TEST
          // tablet's Start class from a real one and skip the auto start (#3569).
          // Always sent, never required: a missing GUID is just left out.
          await scoreApi.ringState.setRingState(
            stationBody({ showId, ring, status, classId, rideId }, getPresenceInstanceId())
          )
        );
        // Merge the server's own response straight in — no extra round
        // trip, and nothing optimistic to unwind if the call had failed.
        setRingStateByRing((prev) => ({ ...prev, [ring]: item }));
        mergeRingCache(showId, ring, item);
      } catch {
        // AC7 — never surfaced. No error banner, no toast to dismiss, no
        // retry prompt. The class this scribe is about to score is
        // completely unaffected either way (AC6).
      }
    },
    [showId, scoreApi]
  );

  const startClass = useCallback(
    (ring, classId) => setExplicit(ring, { status: RING_STATUS.RUNNING, classId }),
    [setExplicit]
  );

  // #2109 (docs/score/ring-truth.md rule 4) — the explicit End action for
  // the current class: writes the class-scoped CLASS_ENDED (#2129), which
  // clears the ring's class but does NOT end the day (END_OF_DAY is Score
  // Admin's declaration), the only other thing besides a later class
  // starting that ends a ring's in-progress status (no timeout).
  // UNLIKE startClass, this is NOT an unconditional overwrite:
  // rule 5 contemplates two scribes sharing a ring, and this hook fetches
  // ring state once on mount with NO polling, so a scribe's screen (and
  // therefore ringStateByRing) can be stale by the time she presses End.
  // Checking the STALE local cache here would be worthless — it's the same
  // wrong data that made the button visible in the first place — so this
  // does one fresh single-ring GET right before the write (the one
  // deliberate exception to this hook's "no extra round trip" rule,
  // scoped to End only; Start still wins unconditionally per rule 4/AC4,
  // and a normal write's own response is still merged straight in with no
  // separate read). Only if that FRESH read still names `classId` as this
  // ring's current occupant does the PUT go out; otherwise it's a silent
  // no-op (AC7's "never surfaced" contract extends here too) and the fresh
  // read is merged into local state regardless, so the screen re-renders to
  // the real, current truth. This NARROWS the race to a single network
  // round trip, it does not close it: there is no conditional write on the
  // server, so a second scribe's Start can still land in the gap between
  // this client's fresh GET and its own PUT and get clobbered right back.
  // For a human-pressed kiosk button that gap is the difference between
  // "stale by minutes to hours, no polling" and "stale by one round trip",
  // real, but not a lock. The atomic fix is a conditional PUT carrying the
  // expected classId (score-api compares-and-swaps instead of trusting the
  // client's read); that's a score-api change, out of scope for this card.
  const endClass = useCallback(
    async (ring, classId) => {
      if (!showId || !ring || !online) return;
      let fresh;
      try {
        fresh = unwrap(await scoreApi.ringState.getRingState(showId, ring));
      } catch {
        // AC7 — a failed pre-check read is silent, same as every other
        // read/write failure in this hook: no error UI, nothing blocks
        // scoring. The class simply stays exactly as shown.
        return;
      }
      setRingStateByRing((prev) => ({ ...prev, [ring]: fresh ?? null }));
      // card #3196: the fresh read is newer than the cache, so it goes there too.
      mergeRingCache(showId, ring, fresh ?? null);
      const truth = resolveRingTruth(fresh);
      if (!truth.inProgress || truth.classId !== classId) return;
      await setExplicit(ring, { status: RING_STATUS.CLASS_ENDED, classId: null, rideId: null });
    },
    [showId, scoreApi, setExplicit, online]
  );

  return {
    isCurrentForRing,
    startClass,
    endClass,
    refetch,
  };
}

export default useRingState;
